import React, { useState } from 'react';
import { locName } from '../../utils/locations';

// Add / edit a site, per the Locations redesign.
//
// Two sections: the site itself, then the contact a driver calls on the day.
// Only name and client are required — everything else can be filled in once the
// site is in use, so the form does not block creating a site you only half know.
//
// The Site ID is read-only: it is assigned on save and fixed thereafter, because
// devices, batches and printed dispatch sheets all reference it.

const SECTION_LABEL = {
  fontSize: 11.5, fontWeight: 600, letterSpacing: '.06em',
  textTransform: 'uppercase', color: 'var(--text-muted)',
};

function Field({ label, value, onChange, placeholder, required, readOnly, hint, invalid }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
      <label style={{ display: 'flex', alignItems: 'baseline', gap: 4, fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>
        {label}
        {required && <span style={{ color: 'var(--brand-light)' }}>*</span>}
      </label>
      <input
        type="text"
        value={value}
        onChange={readOnly ? undefined : e => onChange(e.target.value)}
        readOnly={readOnly}
        placeholder={placeholder}
        aria-invalid={invalid || undefined}
        style={{
          height: 34, padding: '0 10px', borderRadius: 6, outline: 'none',
          border: `1px solid ${invalid ? 'var(--danger-border)' : 'var(--border-default)'}`,
          background: readOnly ? 'var(--surface-inset)' : 'var(--surface-default)',
          color: readOnly ? 'var(--text-muted)' : 'var(--text-primary)',
          fontFamily: 'inherit', fontSize: 14,
        }} />
      {hint && <span style={{ fontSize: 12, color: invalid ? 'var(--danger-text)' : 'var(--text-muted)' }}>{hint}</span>}
    </div>
  );
}

function LocationForm({ loc, existingNames = [], nextSiteId, onSave, onCancel }) {
  const editing = !!loc;
  const [f, setF] = useState(() => ({
    name: editing ? locName(loc) : '',
    customer: (editing && loc.customer) || '',
    address: (editing && loc.address) || '',
    contact: (editing && loc.contact) || '',
    contactRole: (editing && loc.contactRole) || '',
    phone: (editing && loc.phone) || '',
    email: (editing && loc.email) || '',
  }));
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  const name = f.name.trim();
  const customer = f.customer.trim();

  // A duplicate site name breaks the device→location join, which matches on the
  // name string, so it has to be caught here rather than at save time.
  const duplicate = !!name && existingNames.some(
    n => n.toLowerCase() === name.toLowerCase() && !(editing && n === locName(loc))
  );

  const missing = [];
  if (!name) missing.push('site name');
  if (!customer) missing.push('client');
  const canSave = missing.length === 0 && !duplicate;

  const siteIdShown = editing ? (loc.siteId || '—') : (nextSiteId || 'Assigned on save');

  const preview = [
    { k: 'Site', v: name || 'Not named', set: !!name },
    { k: 'Client', v: customer || 'Not set', set: !!customer },
    { k: 'Region', v: f.address.trim() || 'Not set', set: !!f.address.trim() },
    { k: 'Site ID', v: siteIdShown, set: false },
    { k: 'Status', v: editing ? 'Unchanged' : 'Active', set: true },
    { k: 'Devices on site', v: editing ? 'Unchanged' : 'None yet', set: false },
    { k: 'Contact', v: f.contact.trim() || 'Not set', set: !!f.contact.trim() },
  ];

  const submit = () => {
    if (!canSave) return;
    onSave({
      name,
      customer,
      address: f.address.trim(),
      contact: f.contact.trim(),
      contactRole: f.contactRole.trim(),
      phone: f.phone.trim(),
      email: f.email.trim(),
    });
  };

  const gateNote = duplicate
    ? 'Another site already uses that name.'
    : canSave
      ? (editing ? 'Changes apply everywhere this site appears.' : 'Ready to create.')
      : `Needs ${missing.join(' and ')}.`;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ flexShrink: 0, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <button
          type="button"
          onClick={onCancel}
          style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 6, height: 32, padding: '0 12px', borderRadius: 6, border: '1px solid var(--border-default)', background: 'var(--surface-default)', color: 'var(--text-primary)', fontFamily: 'inherit', fontSize: 14, fontWeight: 500, cursor: 'pointer', marginTop: 2 }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.75 }}><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
          Back
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-.3px', color: 'var(--text-primary)' }}>
            {editing ? 'Edit site' : 'Add location'}
          </div>
          <div style={{ fontSize: 14, color: 'var(--text-muted)', marginTop: 4 }}>
            {editing ? 'Changes apply everywhere this site appears.' : 'A new site starts active with nothing on it.'}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div className="card" style={{ flex: '1 1 480px', minWidth: 0, padding: 0, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, padding: '12px 16px', background: 'var(--surface-inset)', borderBottom: '1px solid var(--border-default)' }}>
            <span style={SECTION_LABEL}>Site</span>
            <span style={{ flex: 1, fontSize: 12, color: 'var(--text-muted)' }}>Where the devices go</span>
          </div>
          <div style={{ padding: 16, display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 14, borderBottom: '1px solid var(--border-default)' }}>
            <Field
              label="Site name" required value={f.name} onChange={v => set('name', v)}
              placeholder="Woking Junction"
              invalid={!name || duplicate}
              hint={duplicate ? 'Already in use' : (!name ? 'Required' : '')} />
            <Field
              label="Client" required value={f.customer} onChange={v => set('customer', v)}
              placeholder="Network Rail"
              invalid={!customer}
              hint={!customer ? 'Required' : ''} />
            <Field label="Region" value={f.address} onChange={v => set('address', v)} placeholder="South East" hint="Optional" />
            <Field
              label="Site ID" value={siteIdShown} readOnly
              hint={editing ? 'Fixed once the site exists' : 'Bench assigns the next free ID'} />
          </div>

          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, padding: '12px 16px', background: 'var(--surface-inset)', borderBottom: '1px solid var(--border-default)' }}>
            <span style={SECTION_LABEL}>Site contact</span>
            <span style={{ flex: 1, fontSize: 12, color: 'var(--text-muted)' }}>Who the driver calls on the day</span>
          </div>
          <div style={{ padding: 16, display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 14, borderBottom: '1px solid var(--border-default)' }}>
            <Field label="Contact name" value={f.contact} onChange={v => set('contact', v)} placeholder="Dan Whitcombe" hint="Optional" />
            <Field label="Role" value={f.contactRole} onChange={v => set('contactRole', v)} placeholder="Site supervisor" hint="Optional" />
            <Field label="Phone" value={f.phone} onChange={v => set('phone', v)} placeholder="07700 900123" hint="Optional" />
            <Field label="Email" value={f.email} onChange={v => set('email', v)} placeholder="name@client.co.uk" hint="Optional" />
          </div>

          <div style={{ padding: '12px 16px', background: 'var(--surface-inset)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={submit}
              disabled={!canSave}
              className="btn btn-primary"
              style={{ height: 32, opacity: canSave ? 1 : 0.5, cursor: canSave ? 'pointer' : 'not-allowed' }}>
              {editing ? 'Save changes' : 'Add location'}
            </button>
            <button type="button" onClick={onCancel} className="btn btn-ghost" style={{ height: 32 }}>Cancel</button>
            <span style={{ flex: 1, fontSize: 12, color: canSave ? 'var(--text-muted)' : 'var(--danger-text)' }}>{gateNote}</span>
          </div>
        </div>

        <div style={{ flex: '0 1 320px', minWidth: 280, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ height: 44, display: 'flex', alignItems: 'center', padding: '0 16px', borderBottom: '1px solid var(--border-default)' }}>
              <span style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>
                {editing ? 'This site' : 'The site this creates'}
              </span>
            </div>
            {preview.map(p => (
              <div key={p.k} style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, padding: '9px 16px', borderBottom: '1px solid var(--border-subtle)' }}>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', flexShrink: 0 }}>{p.k}</span>
                <span style={{ fontSize: 14, fontWeight: 500, textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: p.set ? 'var(--text-primary)' : 'var(--text-muted)' }}>{p.v}</span>
              </div>
            ))}
            <div style={{ padding: '12px 16px', background: 'var(--surface-inset)', fontSize: 12, lineHeight: 1.5, color: 'var(--text-muted)' }}>
              {editing
                ? 'Saving updates the site everywhere it appears — the index, deploy destinations and every device sitting on it.'
                : 'The site becomes a deploy destination as soon as it is created. Archive it later rather than deleting it, so its history survives.'}
            </div>
          </div>

          <div className="card" style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
              Required<span style={{ color: 'var(--brand-light)' }}>*</span>
            </div>
            <span style={{ fontSize: 12, lineHeight: 1.5, color: 'var(--text-muted)' }}>
              Site name and client are the only fields needed to create a site — everything else can be filled in once the site is in use. Contact details are what a driver needs on the day, so they are worth completing before the first deployment.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LocationForm;
